import { useEffect, useMemo, useState } from "react";
import api from "../utils/api";
import { useNavigate } from "react-router-dom";
import Select from "react-select";

/* ---------- HELPERS ---------- */

function todayYMD() {
  return new Date().toISOString().slice(0, 10);
}

function toDMY(val) {
  if (!val) return "";
  const [y, m, d] = String(val).split("-");
  if (!y || !m || !d) return val;
  return `${d}/${m}/${y}`;
}

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function cleanStr(v) {
  return String(v ?? "").trim();
}

function toArray(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

function getEmpName(emp) {
  return emp?.emp_name || emp?.name || "";
}

/* ---------- COMPONENT ---------- */

export default function Crush2Add() {
  const navigate = useNavigate();

  const [date, setDate] = useState(todayYMD());
  const [receivedBy, setReceivedBy] = useState("");

  const [sources, setSources] = useState([]);
  const [employees, setEmployees] = useState([]);

  const [rows, setRows] = useState([
    {
      sourceCrush1LineId: "",
      itemCode: "",
      remainingKg: 0,
      machineNo: "",
      operatorName: "",
      timeHour: "",
      resultKg: "",
      receiveDate: "",
    },
  ]);

  /* ---------- LOAD DATA ---------- */

  useEffect(() => {
    api
      .get("/api/crush/crush2/sources/crush1")
      .then((res) => setSources(res.data || []))
      .catch(() => setSources([]));

    api
      .get("/api/employees")
      .then((res) => setEmployees(toArray(res.data)))
      .catch(() => setEmployees([]));
  }, []);

  /* ---------- OPTIONS ---------- */

  const sourceOptions = useMemo(() => {
    return sources.map((s) => ({
      value: String(s.crush1LineId),
      label: `${toDMY(String(s.productionDate || "").slice(0, 10))} | ${
        s.itemCode
      } | Remaining: ${num(s.remainingKg).toFixed(2)} KG`,
    }));
  }, [sources]);

  const employeeOptions = useMemo(() => {
    return employees.map((emp) => ({
      value: getEmpName(emp),
      label: getEmpName(emp),
    }));
  }, [employees]);

  const sourcesById = useMemo(() => {
    const map = new Map();
    sources.forEach((s) => map.set(String(s.crush1LineId), s));
    return map;
  }, [sources]);

  const grandTotal = useMemo(
    () => rows.reduce((s, r) => s + num(r.resultKg), 0),
    [rows]
  );

  /* ---------- FUNCTIONS ---------- */

  function updateRow(i, k, v) {
    setRows((prev) => {
      const copy = [...prev];
      const row = { ...copy[i], [k]: v };

      if (k === "sourceCrush1LineId") {
        const src = sourcesById.get(String(v));

        if (src) {
          row.machineNo = src.machineNo || "";
          row.itemCode = src.itemCode || "";
          row.remainingKg = num(src.remainingKg);
          row.resultKg = "";
        } else {
          row.machineNo = "";
          row.itemCode = "";
          row.remainingKg = 0;
          row.resultKg = "";
        }
      }

      copy[i] = row;
      return copy;
    });
  }

  function addRow() {
    setRows((p) => [
      ...p,
      {
        sourceCrush1LineId: "",
        itemCode: "",
        remainingKg: 0,
        machineNo: "",
        operatorName: "",
        timeHour: "",
        resultKg: "",
        receiveDate: "",
      },
    ]);
  }

  function removeRow(i) {
    setRows((p) => (p.length === 1 ? p : p.filter((_, x) => x !== i)));
  }

  async function save() {
    try {
      if (!date) return alert("Date required");
      if (!receivedBy) return alert("Received By required");

      const lines = rows.map((r) => ({
        sourceCrush1LineId: Number(r.sourceCrush1LineId),
        machineNo: cleanStr(r.machineNo),
        operatorName: cleanStr(r.operatorName),
        timeHour: cleanStr(r.timeHour),
        itemCode: cleanStr(r.itemCode),
        resultKg: num(r.resultKg),
        receiveDate: r.receiveDate,
      }));

      for (const l of lines) {
        if (!l.sourceCrush1LineId) return alert("Please select Source");
        if (!l.machineNo) return alert("Machine is missing");
        if (!l.operatorName) return alert("Please select Operator");
        if (!l.timeHour) return alert("Please enter Time / Hour");
        if (!l.itemCode) return alert("Item Code is missing");
        if (l.resultKg <= 0) return alert("Result / KG must be greater than 0");
        if (!l.receiveDate) return alert("Please select Receive Date");
      }

      await api.post("/api/crush/crush2", {
        productionDate: date,
        receivedBy,
        lines,
      });

      alert("Saved successfully");
      navigate("/crush-2");
    } catch (err) {
      alert(err?.response?.data?.message || err?.message || "Save failed");
    }
  }

  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: "36px",
      height: "36px",
      border: "1px solid #ced4da",
      boxShadow: "none",
      borderRadius: "0.25rem",
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: "36px",
      padding: "0 8px",
    }),
    input: (provided) => ({
      ...provided,
      margin: "0px",
      padding: "0px",
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: "36px",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: "4px",
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: "4px",
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      marginTop: "6px",
      marginBottom: "6px",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      fontSize: "14px",
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#6c757d",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#212529",
    }),
  };

  return (
    <div className="container-fluid p-3">
      <div className="d-flex justify-content-between mb-3 align-items-center">
        <h2 className="mb-0">
          Daily Crush & Recycling Department Production (Crush 2)
        </h2>

        <div>
          <button
            type="button"
            className="btn btn-dark me-2"
            onClick={save}
            title="Save this Crush 2 entry"
          >
            Save
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => navigate("/crush-2")}
            title="Close this page and go back"
          >
            Close
          </button>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label
                className="form-label"
                title="Select production date for this Crush 2 entry"
              >
                Date
              </label>
              <input
                type="date"
                className="form-control"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                title="Production date"
              />
            </div>

            <div className="col-md-5">
              <label
                className="form-label"
                title="Select the employee who received this entry"
              >
                Received By
              </label>
              <Select
                options={employeeOptions}
                value={employeeOptions.find((o) => o.value === receivedBy) || null}
                onChange={(v) => setReceivedBy(v?.value || "")}
                placeholder="-- Select Employee --"
                isClearable
                isSearchable
                styles={customSelectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="table-responsive">
          <table className="table table-bordered align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th title="Serial number" style={{ width: 60 }}>
                  SN
                </th>
                <th
                  title="Select available source from Crush 1 remaining stock"
                  style={{ width: 360 }}
                >
                  Source
                </th>
                <th title="Machine number from selected source" style={{ width: 130 }}>
                  Machine
                </th>
                <th title="Select operator name" style={{ width: 180 }}>
                  Operator
                </th>
                <th title="Enter time or hour" style={{ width: 180 }}>
                  Time
                </th>
                <th title="Item code from selected source" style={{ width: 110 }}>
                  Item
                </th>
                <th title="Available remaining quantity in KG" style={{ width: 120 }}>
                  Remaining
                </th>
                <th title="Enter result quantity in KG" style={{ width: 150 }}>
                  Result
                </th>
                <th title="Select receive date" style={{ width: 170 }}>
                  Receive Date
                </th>
                <th title="Add or remove row" style={{ width: 150 }}>
                  Action
                </th>
              </tr>
            </thead>

            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td className="text-center">{i + 1}</td>

                  <td title="Choose source from Crush 1 remaining material">
                    <Select
                      options={sourceOptions}
                      value={
                        sourceOptions.find(
                          (o) => o.value === r.sourceCrush1LineId
                        ) || null
                      }
                      onChange={(v) =>
                        updateRow(i, "sourceCrush1LineId", v?.value || "")
                      }
                      placeholder="Select..."
                      isClearable
                      isSearchable
                      styles={customSelectStyles}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </td>

                  <td title="Machine auto-filled from selected source">
                    <input
                      className="form-control form-control-sm"
                      value={r.machineNo}
                      readOnly
                      placeholder="Auto"
                      title="Machine from selected source"
                    />
                  </td>

                  <td title="Select operator">
                    <Select
                      options={employeeOptions}
                      value={
                        employeeOptions.find((o) => o.value === r.operatorName) ||
                        null
                      }
                      onChange={(v) =>
                        updateRow(i, "operatorName", v?.value || "")
                      }
                      placeholder="Select..."
                      isClearable
                      isSearchable
                      styles={customSelectStyles}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </td>

                  <td title="Enter working time or hour">
                    <input
                      className="form-control form-control-sm"
                      value={r.timeHour}
                      onChange={(e) =>
                        updateRow(i, "timeHour", e.target.value)
                      }
                      placeholder="e.g. 11:30 / 8"
                      title="Enter time / hour"
                    />
                  </td>

                  <td title="Item auto-filled from selected source">
                    <input
                      className="form-control form-control-sm"
                      value={r.itemCode}
                      readOnly
                      placeholder="Auto"
                      title="Item code from selected source"
                    />
                  </td>

                  <td title="Remaining available KG from selected source">
                    <input
                      className="form-control form-control-sm"
                      value={num(r.remainingKg).toFixed(2)}
                      readOnly
                      title="Remaining KG"
                    />
                  </td>

                  <td title="Enter result quantity in KG">
                    <input
                      className="form-control form-control-sm"
                      value={r.resultKg}
                      onChange={(e) =>
                        updateRow(i, "resultKg", e.target.value)
                      }
                      placeholder="e.g. 200"
                      title="Result quantity in KG"
                    />
                  </td>

                  <td title="Select receive date">
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={r.receiveDate}
                      onChange={(e) =>
                        updateRow(i, "receiveDate", e.target.value)
                      }
                      title="Receive date"
                    />
                  </td>

                  <td>
                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => removeRow(i)}
                        title="Remove this row"
                      >
                        Remove
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-dark"
                        onClick={addRow}
                        title="Add new row"
                      >
                        + Add
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="card-footer d-flex justify-content-between">
          <b title="Total number of rows">Total Records: {rows.length}</b>
          <b title="Sum of all result KG">Grand Total KG: {grandTotal.toFixed(2)}</b>
        </div>
      </div>

      <div className="text-muted mt-2" style={{ fontSize: 12 }}>
        Note: Hover over the labels and fields to see tips.
      </div>
    </div>
  );
}